import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ChatService } from '../../../../service/chat.service';
import { alertModalWithoutConfirm } from '../../../helpers/alert';

@Component({
  selector: 'app-create-group-modal',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './create-group-modal.component.html',
  styleUrls: ['./create-group-modal.component.css']
})
export class CreateGroupModalComponent implements OnInit {
  @Input() userId: any;
  @Input() senderUsername: string = '';
  @Input() profileImg: string = '';
  
  @Output() closeCreateGroupModalEvent = new EventEmitter<void>();
  @Output() groupCreated = new EventEmitter<any>();

  // State
  createGroupStep: number = 1;
  newGroupName: string = "";
  newGroupImage: string = "";
  newDiscription: string = "";
  selectedGroupImageFile: File | null = null;
  selectedGroupMembers: any[] = [];
  availableUsersForGroup: any[] = [];
  userSearchTerm: string = "";
  chatLoader: boolean = false;
  isCreatingGroup: boolean = false;

  constructor(private chatService: ChatService) {}

  ngOnInit() {
    this.fetchAvailableUsers();
  }

  get filteredAvailableUsers() {
    let users = this.availableUsersForGroup;
    if (this.userSearchTerm) {
      const term = this.userSearchTerm.toLowerCase();
      users = users.filter(u =>
        (u.userName && u.userName.toLowerCase().includes(term)) ||
        (u.displayName && u.displayName.toLowerCase().includes(term))
      );
    }
    return users.sort((a, b) => {
      const nameA = (a.displayName || a.userName || '').toLowerCase();
      const nameB = (b.displayName || b.userName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  closeCreateGroupModal() {
    this.closeCreateGroupModalEvent.emit();
  }

  goToNextStep() {
    if (this.selectedGroupMembers.length === 0) {
      alertModalWithoutConfirm("warning", "Please select at least one member.");
      return;
    }
    this.createGroupStep = 2;
  }

  goToPrevStep() {
    this.createGroupStep = 1;
  }

  fetchAvailableUsers() {
    this.chatLoader = true;
    this.chatService.getChats().subscribe({
      next: (resp: any) => {
        const users = resp.data || [];
        this.availableUsersForGroup = users
          .filter((u: any) => u.type !== 'group' && u.userId !== this.userId)
          .sort((a: any, b: any) => {
            const nameA = (a.displayName || a.userName || '').toLowerCase();
            const nameB = (b.displayName || b.userName || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
        this.chatLoader = false;
      },
      error: (err) => {
        console.error("Failed to fetch users", err);
        this.chatLoader = false;
      }
    });
  }

  toggleGroupMemberSelection(user: any) {
    const index = this.selectedGroupMembers.findIndex(m => m.userId === user.userId);
    if (index > -1) {
      this.selectedGroupMembers.splice(index, 1);
    } else {
      this.selectedGroupMembers.push(user);
    }
  }

  onGroupImageSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alertModalWithoutConfirm('warning', 'Only image files are allowed for group profile photo.');
      event.target.value = '';
      return;
    }
    this.selectedGroupImageFile = file;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.newGroupImage = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  createGroupChat() {
    if (!this.newGroupName) {
      alertModalWithoutConfirm("warning", "Please enter a group name.");
      return;
    }

    if (this.selectedGroupMembers.length === 0) {
      alertModalWithoutConfirm("warning", "Please select at least one member.");
      return;
    }

    if (this.newGroupName.length > 100) {
      alertModalWithoutConfirm("warning", "Group name cannot exceed 100 characters.");
      return;
    }

    this.isCreatingGroup = true;

    const members = this.selectedGroupMembers.map(u => ({
      userId: u.userId,
      userName: u.userName,
      displayName: u.displayName,
      profileImage: u.profileImage,
      isAdmin: false
    }));

    const formData = new FormData();
    formData.append('type', 'group');
    formData.append('groupName', this.newGroupName);
    if (this.newDiscription) {
      formData.append('groupDescription', this.newDiscription.trim());
    }

    members.forEach((member, index) => {
      formData.append(`members[${index}][userId]`, member.userId);
      formData.append(`members[${index}][userName]`, member.userName || '');
      formData.append(`members[${index}][displayName]`, member.displayName || '');
      formData.append(`members[${index}][profileImage]`, member.profileImage || '');
      formData.append(`members[${index}][isAdmin]`, String(member.isAdmin));
    });

    if (this.selectedGroupImageFile) {
      formData.append('file', this.selectedGroupImageFile);
    }

    this.chatService.createChat(formData).subscribe({
      next: (res: any) => {
        this.isCreatingGroup = false;
        
        let newChannelId = res?.data?.chat?.channelId || res?.data?.chat?._id || res?.data?.chat?.id || 
                          res?.data?.channelId || res?.data?.chatId || res?.channelId || res?.chatId || 
                          res?.data?.channel_id || res?.data?.chat_id || res?.channel_id || res?.chat_id || 
                          res?.data?._id || res?._id || res?.data?.id || res?.id;
        if (!newChannelId && typeof res?.data === 'string') {
          newChannelId = res.data;
        }

        const fullMembers = [
          {
            userId: this.userId,
            userName: this.senderUsername,
            displayName: this.senderUsername,
            profileImage: this.profileImg || 'assets/user-icon.png',
            isAdmin: true
          },
          ...members
        ];

        const newGroupObj = {
          type: "group",
          channelId: newChannelId,
          groupName: this.newGroupName,
          groupDescription: this.newDiscription ? this.newDiscription.trim() : '',
          groupImg: res?.data?.chat?.groupImg || res?.data?.groupImg || this.newGroupImage || "assets/group_chat.png",
          creator: this.userId,
          members: fullMembers,
          unreadCount: 0,
          lastMessage: {
            message: `You created the group ${this.newGroupName}`,
            createdAt: new Date().toISOString()
          },
          lastMessageTimeAgo: "Just now",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isExited: false
        };

        this.groupCreated.emit(newGroupObj);
        alertModalWithoutConfirm("success", "Group created successfully!");
        this.closeCreateGroupModal();
      },
      error: (err) => {
        console.error("Failed to create group:", err);
        this.isCreatingGroup = false;
        alertModalWithoutConfirm("error", "Failed to create group. Please try again.");
      }
    });
  }

  handleImageError(event: any) {
    event.target.src = 'assets/user-icon.png';
  }

  handleGroupImageError(event: any) {
    event.target.src = 'assets/group_chat.png';
  }
}
